import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import MusicPlayer from "./MusicPlayer";

const ANCHOR_ITEMS = [
  { id: "the-series", label: "The Series" },
  { id: "characters", label: "Characters" },
  { id: "the-initiative", label: "Initiative" },
  { id: "the-choir", label: "Choir" },
  { id: "world", label: "World" },
  { id: "technology", label: "Technology" },
  { to: "/vehicles", label: "Vehicles" },
  { id: "franchise", label: "Franchise" },
  { id: "visual-archive", label: "Archive" },
  { id: "executive-access", label: "Briefing" },
  { id: "contact", label: "Contact" },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleAnchor = (e, id) => {
    e.preventDefault();
    setOpen(false);
    if (window.location.pathname !== "/") {
      window.location.href = `/#${id}`;
      return;
    }
    const el = document.getElementById(id);
    if (!el) return;
    const go = (duration) => {
      if (window.__lenis) {
        window.__lenis.resize();
        window.__lenis.scrollTo(el, { duration, offset: -72 });
      } else {
        el.scrollIntoView({ behavior: duration === 0 ? "instant" : "smooth", block: "start" });
      }
    };
    go(1.4);
    /* Late-loading media can grow the page mid-scroll; if we land far off,
       take one corrective pass once the layout has settled. */
    setTimeout(() => {
      if (Math.abs(el.getBoundingClientRect().top - 72) > 250) go(0.8);
    }, 1700);
    window.history.replaceState(null, "", `#${id}`);
  };

  const handleTop = (e) => {
    e.preventDefault();
    setOpen(false);
    if (window.location.pathname !== "/") {
      window.location.href = "/";
      return;
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
    window.history.replaceState(null, "", "/");
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? "glass-nav" : "bg-transparent"}`}
    >
      <div className="max-w-[1100px] mx-auto px-5 lg:px-8 h-[72px] flex items-center justify-between gap-4">
        <a href="/" data-testid="brand-link" onClick={handleTop} className="flex items-center">
          <img src="/assets/art/cherubim-logo.png" alt="CHERUBIM" className="h-[20px] md:h-[24px] w-auto" />
        </a>

        <nav className="hidden lg:flex items-center gap-5">
          {ANCHOR_ITEMS.map((item) => (
            item.to ? (
              <Link
                key={item.to}
                to={item.to}
                data-testid="nav-vehicles"
                className="font-caps text-[11px] text-white/75 hover:text-[var(--cb-gold)] transition-colors duration-300"
              >
                {item.label}
              </Link>
            ) : (
              <a
                key={item.id}
                href={`#${item.id}`}
                data-testid={`nav-${item.id}`}
                onClick={(e) => handleAnchor(e, item.id)}
                className="font-caps text-[11px] text-white/75 hover:text-[var(--cb-gold)] transition-colors duration-300"
              >
                {item.label}
              </a>
            )
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <MusicPlayer />
          <button
            aria-label="Toggle menu"
            data-testid="nav-menu-toggle"
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden text-white"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden glass-nav border-t border-white/5">
          <div className="px-5 py-6 flex flex-col gap-4 max-h-[80vh] overflow-y-auto">
            {ANCHOR_ITEMS.map((item) => (
              item.to ? (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid="m-nav-vehicles"
                  onClick={() => setOpen(false)}
                  className="font-caps text-[13px] text-white/85"
                >
                  {item.label}
                </Link>
              ) : (
                <a
                  key={item.id}
                  href={`#${item.id}`}
                  data-testid={`m-nav-${item.id}`}
                  onClick={(e) => handleAnchor(e, item.id)}
                  className="font-caps text-[13px] text-white/85"
                >
                  {item.label}
                </a>
              )
            ))}
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
